import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import Store from "@/store";
import Login from "@/views/account/Login.vue";
import ResetPassword from "@/views/account/ResetPassword.vue";
import { getModule } from "vuex-module-decorators";
import AuthModule from "@/store/clients/Auth.module";
import UserModule from "@/store/clients/User.module";

const Auth = getModule(AuthModule, Store);
const User = getModule(UserModule, Store);

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/login",
    name: "login",
    meta: {
      title: "Login",
    },
    component: Login,
  },
  {
    path: "/Account/ResetPassword",
    name: "ResetPassword",
    meta: {
      title: "Reset Password",
    },
    component: ResetPassword,
  },
  {
    path: "/",
    name: "Dashboard",
    meta: {
      title: "Dashboard",
    },
    component: () => import("../views/Dashboard.vue"),
  },
  {
    path: "/VesselOverview",
    name: "VesselOverview",
    meta: {
      title: "Vessel Overview",
    },
    component: () => import("../views/VesselOverview.vue"),
  },
  {
    path: "/FleetOverview",
    name: "FleetOverview",
    meta: {
      title: "Fleet Overview",
    },
    component: () => import("../views/FleetOverview.vue"),
  },
  {
    path: "/VesselComparison",
    name: "VesselComparison",
    meta: {
      title: "Vessel Comparison",
    },
    component: () => import("../views/VesselComparisonView.vue"),
  },
  {
    path: "/Help",
    component: () => import("../views/help/HelpPage.vue"),
    children: [
      { path: "", redirect: { name: "VersionHistory" } },
      {
        path: "VersionHistory",
        name: "VersionHistory",
        meta: { title: "Version History | Help" },
        component: () => import("../views/help/VersionHistory.vue"),
      },
      {
        path: "GettingStarted",
        meta: { title: "Getting Started | Help" },
        component: () => import("../views/help/GettingStarted.vue"),
      },
      {
        path: "CII",
        meta: { title: "CII | Help" },
        component: () => import("../views/help/CII.vue"),
      },
      {
        path: "Basic",
        meta: { title: "Basic | Help" },
        component: () => import("../views/help/Basic.vue"),
      },
      {
        path: "Diagnostics",
        meta: { title: "Diagnostics | Help" },
        component: () => import("../views/help/Diagnostics.vue"),
      },
      {
        path: "Fouling",
        meta: { title: "Fouling | Help" },
        component: () => import("../views/help/Fouling.vue"),
      },
      {
        path: "Compare",
        meta: { title: "Compare | Help" },
        component: () => import("../views/help/Compare.vue"),
      },
      {
        path: "CharterParty",
        meta: { title: "Charter Party | Help" },
        component: () => import("../views/help/CharterParty.vue"),
      },
      {
        path: "Notifications",
        meta: { title: "Notifications | Help" },
        component: () => import("../views/help/Notifications.vue"),
      },
      {
        path: "EmissionReport",
        meta: { title: "Emission Report | Help" },
        component: () => import("../views/help/EmissionReport.vue"),
      },
      {
        path: "FAQ",
        meta: { title: "FAQ | Help" },
        component: () => import("../views/help/FAQ.vue"),
      },
      {
        path: "ISO 19030",
        meta: { title: "ISO 19030 | Help" },
        component: () => import("../views/help/Iso19030.vue"),
      },
      {
        path: "DataQuality",
        meta: { title: "DataQuality | Help" },
        component: () => import("../views/help/DataQuality.vue"),
      },
    ],
  },
  {
    path: "/Vessel/:vesselId/:vesselName",
    component: () => import("../views/vessel/Vessel.vue"),
    children: [
      { path: "", redirect: { name: "Info" } },
      { path: "Info", name: "Info", meta: { subTitle: "Info", title: "Info", parent: "Vessel" }, component: () => import("../views/vessel/VesselInfo.vue") },
      { path: "MachineLearningModels", name: "VesselMachineLearningModels", meta: { subTitle: "Machine Learning Models", title: "Machine Learning Models", parent: "Vessel" }, component: () => import("../views/vessel/VesselMachineLearningModels.vue") },
      { path: "Notes", name: "Notes", meta: { subTitle: "Notes", title: "Notes", parent: "Vessel" }, component: () => import("../views/vessel/VesselNotes.vue") },
      { path: "VesselFiles", name: "VesselFiles", meta: { subTitle: "Documents", title: "Documents", parent: "Vessel" }, component: () => import("../views/vessel/VesselFiles.vue") },
      { path: "Report", name: "Report", meta: { subTitle: "Reports", title: "Report", parent: "Vessel" }, component: () => import("../views/vessel/Report.vue") },
      { path: "Shapoli", name: "Shapoli", meta: { subTitle: "EEXI ShaPoLi", title: "EEXI ShaPoLi", parent: "Vessel" }, component: () => import("../views/vessel/Shapoli.vue") },
      { path: "LoggingHistory", name: "LoggingHistory", meta: { subTitle: "Logging History", title: "Logging History", parent: "Vessel" }, component: () => import("../views/vessel/LoggingHistory.vue") },
      { path: "DataHistory", name: "DataHistory", meta: { subTitle: "Data History", title: "Data History", parent: "Vessel" }, component: () => import("../views/vessel/DataHistory.vue") },
      { path: "LongTrend", name: "LongTrend", meta: { subTitle: "Long Trend", title: "Long Trend", parent: "Vessel" }, component: () => import("../views/vessel/LongTrend.vue") },
      { path: "Diagnostics", name: "Diagnostics", meta: { subTitle: "Diagnostics", title: "Diagnostics", parent: "Vessel" }, component: () => import("../views/vessel/Diagnostics/index.vue") },
      { path: "ISO19030", name: "ISO19030", meta: { subTitle: "ISO19030", title: "ISO19030", parent: "Vessel" }, component: () => import("../views/vessel/ISO19030.vue") },
      { path: "EmissionsCompliance", name: "EmissionsCompliance", meta: { subTitle: "Emissions Compliance", title: "Emissions Compliance", parent: "Vessel" }, component: () => import("../views/vessel/EmissionsCompliance.vue") },
      { path: "FeatureMapping", name: "FeatureMapping", meta: { subTitle: "Feature Mapping", title: "Feature Mapping", parent: "Vessel" }, component: () => import("../views/vessel/FeatureMapping.vue") },
      { path: "Fouling/Diagnostics", name: "DiagnosticsFouling", meta: { subTitle: "DiagnosticsFouling", title: "DiagnosticsFouling", parent: "Vessel" }, component: () => import("../views/vessel/DiagnosticsFouling.vue") },
      { path: "EmissionReport", name: "EmissionReport", meta: { subTitle: "Emission Report", title: "Emission Report", parent: "Vessel" }, component: () => import("../views/vessel/EmissionReport.vue") },
      { path: "CharterParty", name: "CharterParty", meta: { subTitle: "Charter Party", title: "Charter Party", parent: "Vessel" }, component: () => import("../views/vessel/CharterParty.vue") },
      { path: "DataAnalysis", name: "DataAnalysis", meta: { subTitle: "Data Analysis", title: "Data Analysis", parent: "Vessel" }, component: () => import("../views/vessel/DataAnalysis.vue") },
    ],
  },
  {
    path: "/Settings",
    component: () => import("../views/Settings/index.vue"),
    children: [
      { path: "", redirect: { name: "UserList" } },
      { path: "UserList", name: "UserList", meta: { title: "User List" }, component: () => import("../views/Settings/UserList.vue") },
      { path: "UserListOLD", name: "UserListOLD", meta: { title: "User List OLD" }, component: () => import("../views/profile/Users.vue") },
      { path: "CompanyList", name: "CompanyList", meta: { title: "Company List" }, component: () => import("../views/Settings/CompanyList.vue") },
      { path: "CompanyListOLD", name: "CompanyListOLD", meta: { title: "Company List OLD" }, component: () => import("../views/company/CompanyManager.vue") },
      { path: "VesselAccessControl", name: "VesselAccessControl", meta: { title: "Vessel Access Control" }, component: () => import("../views/Settings/VesselAccessControl.vue") },
      { path: "VesselAccessControlOLD", name: "VesselAccessControlOLD", meta: { title: "Vessel Access Control OLD" }, component: () => import("../views/vesseladmin/VesselAccessControl.vue") },
      { path: "VesselGroups", name: "VesselGroups", meta: { title: "Vessel Groups", }, component: () => import("../views/vesseladmin/VesselGroups.vue") },
      { path: "ManageVessels", name: "ManageVessels", meta: { title: "Manage Vessels" }, component: () => import("../views/Settings/ManageVessels.vue") },
      { path: "ManageVesselsOLD", name: "ManageVesselsOLD", meta: { title: "Manage Vessels OLD" }, component: () => import("../views/vesseladmin/VesselAdministration.vue") },
      { path: "ReferenceCurves", name: "ReferenceCurves", meta: { title: "Reference Curves" }, component: () => import("../views/referencecurves/index.vue") },
      { path: "Announcement", name: "Announcement", meta: { title: "Announcement" }, component: () => import("../views/Announcement/AnnouncementTableView.vue") },
      { path: "Taxonomy", name: "Taxonomy", meta: { title: "Taxonomy" }, component: () => import("../views/Taxonomy.vue") },
      { path: "ImportStatus", name: "ImportStatus", meta: { title: "Import Status" }, component: () => import("../views/importstatus/ImportStatus.vue") },
    ],
  },
  {
    path: "/Users",
    name: "Users",
    meta: {
      title: "Users",
    },
    component: () => import("../views/profile/Users.vue"),
  },
  {
    path: "/MyProfile",
    name: "MyProfile",
    meta: {
      title: "Profile",
    },
    component: () => import("../views/profile/MyProfile.vue"),
  },
  {
    path: "/Company",
    name: "company",
    meta: {
      title: "Company",
    },
    component: () => import("../views/company/CompanyManager.vue"),
  },
  {
    path: "/DataQuality/",
    component: () => import("../views/dataQuality/DataQuality.vue"),
    children: [
      { path: "", redirect: { name: "DQFleetOverview" } },
      {
        path: "DQFleetOverview",
        name: "DQFleetOverview",
        meta: {
          subTitle: "FleetOverview",
          title: "Fleet Overview",
          parent: "DataQuality",
        },
        component: () => import("../views/dataQuality/FleetOverview.vue"),
      },
      {
        path: "DataAvailability",
        name: "DataAvailability",
        meta: {
          subTitle: "DataAvailability",
          title: "Data Availability",
          parent: "DataQuality",
        },
        component: () => import("../views/dataQuality/DataAvailability.vue"),
      },
      {
        path: "DataOutliers",
        name: "DataOutliers",
        meta: {
          subTitle: "DataOutliers",
          title: "Data Outliers",
          parent: "DataQuality",
        },
        component: () => import("../views/dataQuality/DataOutliers.vue"),
      },
      {
        path: "SpeedLog",
        name: "SpeedLog",
        meta: {
          subTitle: "SpeedLog",
          title: "Speed Log",
          parent: "DataQuality",
        },
        component: () => import("../views/dataQuality/SpeedLog.vue"),
      },
      {
        path: "DataAlignment",
        name: "DataAlignment",
        meta: {
          subTitle: "DataAlignment",
          title: "Data Alignment",
          parent: "DataQuality",
        },
        component: () => import("../views/dataQuality/DataAlignment.vue"),
      },
      {
        path: "MachineLearningModels",
        name: "MachineLearningModels",
        meta: {
          subTitle: "Machine Learning Models",
          title: "Machine Learning Models",
          parent: "DataQuality",
        },
        component: () => import("../views/dataQuality/MachineLearningModels.vue"),
      },
    ],
  },
  {
    path: "/VesselAdministration",
    name: "VesselAdministration",
    meta: {
      title: "Vessel Administration",
    },
    component: () => import("../views/vesseladmin/VesselAdministration.vue"),
  },
  {
    path: "/Alerts",
    name: "Alerts",
    meta: {
      title: "Alerts",
    },
    component: () => import("../views/Alerts/Alerts.vue"),
  },
  {
    path: "/Alerts/New",
    name: "NewAlert",
    meta: {
      title: "New Alert",
    },
    component: () => import("../views/Alerts/NewAlert.vue"),
  },
  {
    path: "/Notifications",
    name: "Notifications",
    meta: {
      title: "Notifications",
    },
    component: () => import("../views/MyNotifications/index.vue"),
  },
  {
    path: "/Notifications/New",
    name: "AddAlert",
    meta: {
      title: "New Alert",
    },
    component: () => import("../views/MyNotifications/AddNotification.vue"),
  },
  {
    path: "/About",
    name: "About",
    meta: {
      title: "About",
    },
    component: () => import("../views/About.vue"),
  },
  {
    path: "/Kognifai",
    name: "Kognifai",
    meta: {
      title: "Kognifai",
    },
    component: () => import("../views/kognifai/DataAnalysis.vue"),
    beforeEnter(to, from, next) {
      if (User.isKognifai) {
        next();
      } else {
        next("/");
      }
    },
  },
  {
    path: "*",
    name: "NotFound",
    meta: {
      title: "Not Found",
    },
    component: () => import("../views/NotFound.vue"),
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeEach((to, from, next) => {
  const publicPages = ["/login", "/Account/ResetPassword"];
  const authRequired = !publicPages.includes(to.path);

  // trying to access a restricted page + not logged in
  // redirect to login page
  if (authRequired && !Auth.isLoggedIn) {
    return next("/login");
    //make sure user is valid and exists
  } else if (authRequired && User.userExpired) {
    User.getUser()
      .then(() => {
        if (User.isKognifai && to.name !== "Kognifai") {
          return next("/Kognifai");
        } else {
          return next();
        }
      })
      .catch(() => { });
  } else if (User.isKognifai && to.name !== "Kognifai") {
    return next("/Kognifai");
  } else {
    return next();
  }
});

router.afterEach((to, from) => {
  if (to.meta?.parent === "Vessel") {
    document.title = to.meta.title + " | " + to.params.vesselName + " | GTT Marine Fleet Insights";
  } else {
    document.title = to.meta?.title + " | GTT Marine Fleet Insights";
  }
});

export default router;
